import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ColDef, GridReadyEvent } from "ag-grid-community";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import type { QueryState } from "components/ui-common/AgGridTable/AgGridTable";
import { useInventory, useInventoryStore } from "hooks";
import {
  Inventory,
  InventoryForm,
  InventoryQuery,
  AccountCode,
  WmsCode,
  WmsItem,
  WmsItemGroup,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import { formatCurrency, parseNumberSafe } from "utils/helpers/accounting";
import {
  accountCodeService,
  wmsCodeService,
  wmsItemService,
  wmsItemGroupService,
} from "services";
import { parseNumber } from "utils/helpers/data_helpers";

type PersistMeta = {
  pinned?: boolean;
  onRevert?: () => void;
  onSuccess?: () => void;
  refetch?: boolean;
};

// const parseNumber = (value: unknown): number | null => {
//   if (value === null || value === undefined || value === "") return null;
//   const parsed = parseNumberSafe(value);
//   return Number.isFinite(parsed) ? parsed : null;
// };

const OpeningInventories: React.FC = () => {
  const gridApiRef = useRef<GridReadyEvent["api"] | null>(null);
  const addRowRef = useRef<Inventory | null>(null);
  const [addRowVersion, setAddRowVersion] = useState(0);
  const [queryParams, setQueryParams] = useState<InventoryQuery>({
    page: 1,
    pageSize: 20,
  });
  const [wmsCodes, setWmsCodes] = useState<WmsCode[]>([]);
  const [itemGroups, setItemGroups] = useState<WmsItemGroup[]>([]);
  const [wmsItems, setWmsItems] = useState<WmsItem[]>([]);
  const [accountCodes, setAccountCodes] = useState<AccountCode[]>([]);
  const [showInlineAddRow, setShowInlineAddRow] = useState<any>("");
  const [confirmRow, setConfirmRow] = useState<Inventory | null>(null);

  const {
    rows,
    setRows,
    upsertRow,
    removeRow,
    editingId,
    setEditingId,
    isLoading,
    setLoading,
  } = useInventoryStore();
  const { fetchList, createInventory, updateInventory, deleteInventory } =
    useInventory();

  const {
    data: inventoryData,
    isFetching,
    refetch,
  } = fetchList(queryParams, true);

  const sanitizeRow = useCallback(
    (row: Inventory): Inventory => {
      const quantity = parseNumberSafe(row.quantity);
      const unitPrice = parseNumberSafe(row.unit_price);
      return {
        ...row,
        wms_id: parseNumberSafe(row.wms_id),
        category_id:
          row.category_id !== undefined && row.category_id !== null
            ? parseNumberSafe(row.category_id)
            : null,
        product_id: parseNumberSafe(row.product_id),
        unit_id: parseNumberSafe(row.unit_id),
        quantity,
        unit_price: unitPrice,
        amount:
          row.amount !== undefined && row.amount !== null
            ? parseNumberSafe(row.amount)
            : quantity * unitPrice,
        convert_unit_id:
          row.convert_unit_id !== undefined && row.convert_unit_id !== null
            ? parseNumberSafe(row.convert_unit_id)
            : null,
        convert_ratio:
          row.convert_ratio !== undefined && row.convert_ratio !== null
            ? parseNumberSafe(row.convert_ratio)
            : null,
        convert_quantity:
          row.convert_quantity !== undefined && row.convert_quantity !== null
            ? parseNumberSafe(row.convert_quantity)
            : null,
      };
    },
    [],
  );

  useEffect(() => {
    if (inventoryData?.data) {
      const sanitized = (inventoryData.data as Inventory[]).map(sanitizeRow);
      setRows(sanitized);
    }
  }, [inventoryData, sanitizeRow, setRows, isFetching]);

  useEffect(() => {
    setLoading(
      Boolean(
        isFetching ||
        createInventory.isPending ||
        updateInventory.isPending ||
        deleteInventory.isPending,
      ),
    );
  }, [
    isFetching,
    createInventory.isPending,
    updateInventory.isPending,
    deleteInventory.isPending,
    setLoading,
  ]);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      wmsCodeService.list(),
      wmsItemGroupService.list(),
      wmsItemService.list(),
      accountCodeService.list({ page: 1, pageSize: 1000, type: "unit" }),
    ])
      .then(([wmsData, itemGroupData, wmsItemsData, accountCodeRes]) => {
        if (!mounted) return;
        setWmsCodes(wmsData ?? []);
        setItemGroups(itemGroupData ?? []);
        setWmsItems(wmsItemsData ?? []);
        const accountList =
          accountCodeRes?.data && Array.isArray(accountCodeRes.data)
            ? accountCodeRes.data
            : [];
        setAccountCodes(accountList as AccountCode[]);
      })
      .catch(() => {
        if (!mounted) return;
        setWmsCodes([]);
        setItemGroups([]);
        setWmsItems([]);
        setAccountCodes([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const columns = useMemo<ColDef<Inventory>[]>(() => {
    const numberParser = (params: any) => parseNumberSafe(params.newValue);
    const numberEditor = "agNumberCellEditor";
    const wmsValues = wmsCodes.map((item) => String(item.id));
    const wmsDisplay = new Map(
      wmsCodes.map((item) => [
        String(item.id),
        `${item.code}${item.name ? ` - ${item.name}` : ""}`,
      ]),
    );
    const itemGroupValues = itemGroups.map((item) => String(item.id));
    const itemGroupDisplay = new Map(
      itemGroups.map((item) => [
        String(item.id),
        `${item.code}${item.name ? ` - ${item.name}` : ""}`,
      ]),
    );
    const productValues = wmsItems.map((item) => item.id);
    const productDisplay = new Map(
      wmsItems.map((item) => [
        String(item.id),
        `${item.code}${item.name ? ` - ${item.name}` : ""}`,
      ]),
    );
    const unitValues = accountCodes.map((item) => String(item.id));
    const unitDisplay = new Map(
      accountCodes.map((item) => [String(item.id), `${item.name ?? ""}`]),
    );
    // const accountValues = accountCodes.map((item) => String(item.id));
    // const accountDisplay = new Map(
    //   accountCodes.map((item) => [
    //     String(item.id),
    //     `${item.code}${item.name ? ` - ${item.name}` : ""}`,
    //   ]),
    // );

    return [
      {
        headerName: "Kho (wms_id)",
        field: "wms_id",
        minWidth: 140,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: wmsValues },
        valueFormatter: (params) =>
          wmsDisplay.get(String(params.value)) ?? params.value,
        valueParser: (params) => parseNumber(params.newValue),
      },
      {
        headerName: "Danh mục",
        field: "category_id",
        minWidth: 140,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: itemGroupValues },
        valueFormatter: (params) =>
          itemGroupDisplay.get(String(params.value)) ?? params.value,
        valueParser: (params) => parseNumber(params.newValue),
      },
      {
        headerName: "Mã hàng hóa",
        field: "product_id",
        minWidth: 140,
        editable: true,
        cellDataType: "number",
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: productValues },
        valueFormatter: (params) =>
          productDisplay.get(String(params.value)) ?? params.value,
        valueParser: (params) => parseNumber(params.newValue),
        valueSetter: (params) => {
          const nextValue = parseNumber(params.newValue);
          const selected = wmsItems.find(
            (item) => String(item.id) === String(nextValue),
          );
          (params.data as Inventory).product_id = nextValue ?? null;
          (params.data as Inventory).product_name = selected?.name ?? "";
          return true;
        },
      },
      {
        headerName: "Tên hàng hóa",
        field: "product_name",
        flex: 1,
        minWidth: 180,
        cellEditor: "agTextCellEditor",
      },
      {
        headerName: "Đơn vị (unit_id)",
        field: "unit_id",
        minWidth: 140,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: unitValues },
        valueFormatter: (params) =>
          unitDisplay.get(String(params.value)) ?? params.value,
        valueParser: (params) => parseNumberSafe(params.newValue),
      },
      {
        headerName: "Số lượng",
        field: "quantity",
        minWidth: 140,
        cellDataType: "number",
        cellEditor: numberEditor,
        valueParser: numberParser,
        valueFormatter: (params) => formatCurrency(params.value, false),
      },
      {
        headerName: "Đơn giá",
        field: "unit_price",
        minWidth: 140,
        cellDataType: "number",
        cellEditor: numberEditor,
        valueParser: numberParser,
        valueFormatter: (params) => formatCurrency(params.value, false),
      },
      {
        headerName: "Thành tiền",
        field: "amount",
        minWidth: 150,
        editable: false,
        valueGetter: (params) => {
          const quantity = parseNumberSafe(params.data?.quantity);
          const unitPrice = parseNumberSafe(params.data?.unit_price);
          const amount =
            params.data?.amount !== undefined && params.data?.amount !== null
              ? parseNumberSafe(params.data.amount)
              : quantity * unitPrice;
          return amount;
        },
        valueFormatter: (params) =>
          formatCurrency(params.value),
      },
      {
        headerName: "Đơn vị quy đổi",
        field: "convert_unit_id",
        minWidth: 140,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: unitValues },
        valueFormatter: (params) =>
          unitDisplay.get(String(params.value)) ?? params.value,
        valueParser: (params) => parseNumberSafe(params.newValue),
      },
      {
        headerName: "Toán tử quy đổi",
        field: "convert_operator",
        minWidth: 140,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: ["", "*", "/"],
        },
      },
      {
        headerName: "Tỉ lệ quy đổi",
        field: "convert_ratio",
        minWidth: 140,
        cellDataType: "number",
        cellEditor: numberEditor,
        valueParser: numberParser,
      },
      {
        headerName: "Số lượng quy đổi",
        field: "convert_quantity",
        minWidth: 160,
        cellDataType: "number",
        cellEditor: numberEditor,
        valueParser: numberParser,
        valueFormatter: (params) => formatCurrency(params.value, false),
      },
    ];
  }, [accountCodes, itemGroups, wmsCodes, wmsItems]);

  const normalizePayload = useCallback(
    (row: Partial<Inventory>): InventoryForm => {
      const productId = parseNumber(row.product_id);
      const selectedProduct = wmsItems.find(
        (item) => String(item.id) === String(productId),
      );
      const quantity = parseNumber(row.quantity);
      const unitPrice = parseNumber(row.unit_price);
      const amount =
        row.amount !== undefined
          ? parseNumber(row.amount)
          : quantity * unitPrice;

      return {
        wms_id: parseNumber(row.wms_id),
        category_id:
          row.category_id !== undefined
            ? parseNumber(row.category_id)
            : undefined,
        product_id: productId,
        product_name:
          row.product_name && String(row.product_name).trim()
            ? String(row.product_name).trim()
            : (selectedProduct?.name ?? ""),
        unit_id: parseNumber(row.unit_id),
        quantity,
        unit_price: unitPrice,
        amount,
        convert_unit_id:
          row.convert_unit_id !== undefined
            ? parseNumber(row.convert_unit_id)
            : undefined,
        convert_operator: row.convert_operator
          ? String(row.convert_operator).trim()
          : undefined,
        convert_ratio:
          row.convert_ratio !== undefined
            ? parseNumber(row.convert_ratio)
            : undefined,
        convert_quantity:
          row.convert_quantity !== undefined
            ? parseNumber(row.convert_quantity)
            : undefined,
      };
    },
    [wmsItems],
  );

  const resetInlineAddRow = useCallback(() => {
    addRowRef.current = {
      wms_id: null,
      category_id: null,
      product_id: null,
      product_name: "",
      unit_id: null,
      quantity: null,
      unit_price: null,
      amount: null,
      convert_unit_id: null,
      convert_operator: null,
      convert_ratio: null,
      convert_quantity: null,
    } as Inventory;
    setAddRowVersion((v) => v + 1);
  }, []);

  const persistRow = useCallback(
    (row: Partial<Inventory>, meta?: PersistMeta) => {
      const payload = normalizePayload(row);

      if (
        !payload.wms_id ||
        !payload.product_id ||
        !payload.unit_id ||
        !payload.quantity
      ) {
        SnackbarUtils.error("Kho, Hàng hóa, Đơn vị và Số lượng là bắt buộc");
        meta?.onRevert?.();
        return;
      }

      const shouldCreate = meta?.pinned || !row.id;
      if (shouldCreate) {
        createInventory.mutate(payload, {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(sanitizeRow(res.data as Inventory));
            }
            resetInlineAddRow();
            if (meta?.refetch !== false) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Thêm tồn kho đầu kỳ không thành công");
            meta?.onRevert?.();
          },
        });
        return;
      }

      if (!row.id) {
        SnackbarUtils.error("Không xác định được dòng cần cập nhật");
        meta?.onRevert?.();
        return;
      }

          updateInventory.mutate(
            { id: row.id, ...payload },
            {
              onSuccess: (res) => {
                if (res?.data) {
                  upsertRow(sanitizeRow(res.data as Inventory));
                } else {
                  upsertRow(sanitizeRow({ ...(row as Inventory), ...payload }));
                }
                if (meta?.refetch) {
                  refetch();
                }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Cập nhật tồn kho đầu kỳ không thành công");
            meta?.onRevert?.();
          },
        },
      );
    },
    [
      createInventory,
      normalizePayload,
      refetch,
      resetInlineAddRow,
      updateInventory,
      upsertRow,
    ],
  );

  const handleSaveRow = useCallback(
    (row: Inventory, meta?: { pinned?: boolean }) => {
      persistRow(row, {
        pinned: meta?.pinned,
        refetch: meta?.pinned || !row.id,
      });
    },
    [persistRow],
  );

  const handleEditRow = useCallback(
    (params) => {
      setShowInlineAddRow("edit");
      setEditingId(params.data.id);
    },
    [setEditingId],
  );

  const handleDeleteRow = useCallback((row: Inventory) => {
    setConfirmRow(row);
    setShowInlineAddRow("");
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!confirmRow) return;
    deleteInventory.mutate(confirmRow.id, {
      onSuccess: () => {
        removeRow(confirmRow.id);
        if (editingId === confirmRow.id) {
          setEditingId(null);
        }
        setConfirmRow(null);
      },
      onError: () => {
        SnackbarUtils.error("Xóa tồn kho đầu kỳ không thành công");
      },
    });
  }, [confirmRow, deleteInventory, editingId, removeRow, setEditingId]);

  const toggleInlineAddRow = useCallback(() => {
    setShowInlineAddRow((prev) => {
      const next = !prev ? "add" : "";
      if (next) {
        resetInlineAddRow();
      } else {
        addRowRef.current = null;
        setAddRowVersion((v) => v + 1);
      }
      return next;
    });
  }, [resetInlineAddRow]);

  const handleQueryChange = useCallback(
    (query: QueryState) => {
      setQueryParams((prev) => {
        const next = {
          ...prev,
          page: query.pagination?.page ?? prev.page,
          pageSize: query.pagination?.pageSize ?? prev.pageSize,
          sortModel: query.sortModel,
          filterModel: query.filterModel,
        };
        const unchanged =
          next.page === prev.page &&
          next.pageSize === prev.pageSize &&
          next.sortModel === prev.sortModel &&
          next.filterModel === prev.filterModel;
        return unchanged ? prev : next;
      });
    },
    [setQueryParams],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      flex: 1,
      minWidth: 140,
    }),
    [],
  );

  const inlineAddRowData = useMemo(
    () =>
      showInlineAddRow === "add" ? (addRowRef.current ?? undefined) : undefined,
    [showInlineAddRow, addRowVersion],
  );

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">
          Opening Inventories
        </h1>
        <p className="text-sm text-gray-600">
          Quản lý tồn kho đầu kỳ, thêm nhanh và chỉnh sửa trực tiếp trên bảng.
        </p>
      </div>

      <div className="relative bg-white border border-gray-200 shadow-sm rounded-lg p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Danh sách tồn kho đầu kỳ
            </h2>
            <p className="text-xs text-gray-500">
              Sử dụng grid để tạo mới, cập nhật hoặc xóa trực tiếp.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggleInlineAddRow}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {showInlineAddRow ? "Ẩn dòng thêm mới" : "Thêm nhanh trên bảng"}
            </button>
          </div>
        </div>
        <AgGridTable
          rows={rows}
          columns={columns}
          height={560}
          autoHeight={false}
          showAddRow={showInlineAddRow}
          addRowData={inlineAddRowData}
          defaultColDef={defaultColDef}
          totalPages={inventoryData?.totalPage}
          onEdit={handleEditRow}
          onDelete={handleDeleteRow}
          onSave={handleSaveRow}
          onQueryChange={handleQueryChange}
          getRowId={(params) => String(params.data.id)}
          onGridReady={(event) => {
            gridApiRef.current = event.api;
          }}
          onAfterActions={() => {
            setShowInlineAddRow("");
          }}
        />
        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang cập nhật danh sách tồn kho đầu kỳ, vui lòng chờ giây lát."
          />
        )}
      </div>
      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa tồn kho đầu kỳ?"
        description={`Bạn chắc chắn muốn xóa dòng tồn kho "${confirmRow?.product_name ?? ""}"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={deleteInventory.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default OpeningInventories;
